import type { ReactNode } from 'react';

import { type AuthAside, AuthPanel } from './AuthPanel.tsx';

/**
 * Split-screen auth chrome shared by curator/company sign-in and company
 * sign-up: wordmark + form on the left, dark brand panel on the right
 * (stacked under the form below lg). Expects /assets/remote-medium-wordmark.svg in the host
 * app's public dir. The candidate pages in apps/web mirror this markup in
 * layouts/AuthShell.astro and render the same AuthPanel.
 */
export function AuthLayout({
  title,
  intro,
  width = 'sm',
  aside,
  siteUrl,
  children,
  footer,
  altLink,
}: {
  title: string;
  intro?: ReactNode;
  width?: 'sm' | 'md';
  aside?: AuthAside | undefined;
  /** Public site origin; the header logo and "Back to site" link go there. */
  siteUrl?: string | undefined;
  children: ReactNode;
  footer?: ReactNode;
  /** Small cross-link to the other audience's sign-in, under the footer. */
  altLink?: ReactNode;
}) {
  const site = siteUrl?.replace(/\/$/, '') ?? '';
  return (
    <div className="grid min-h-dvh grid-rows-[1fr_auto] lg:grid-rows-1 bg-background text-foreground lg:grid-cols-2 lg:gap-4 lg:p-4">
      <div className="flex flex-col">
        <header className="flex items-center justify-between gap-6 px-4 py-6 sm:px-8 lg:px-6 lg:py-4">
          <a href={site || '/'} aria-label="Remote Medium home" className="flex">
            <img
              src="/assets/remote-medium-wordmark.svg"
              alt="Remote Medium"
              width={152}
              height={36}
              className="block h-9 w-auto"
            />
          </a>
          {siteUrl && (
            <a
              href={site}
              className="text-sm text-muted-foreground no-underline hover:text-foreground hover:underline"
            >
              Back to site
            </a>
          )}
        </header>
        <main className="flex max-w-none flex-1 items-center justify-center px-4 py-10 sm:px-8">
          <div className={width === 'md' ? 'w-full max-w-[480px]' : 'w-full max-w-[380px]'}>
            <h1 className="m-0 font-serif text-3xl leading-tight font-medium tracking-tight md:text-4xl">
              {title}
            </h1>
            {intro && (
              <p className="mt-4 mb-0 text-base leading-6 text-muted-foreground">{intro}</p>
            )}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
            {altLink && (
              <p className="m-0 mt-8 border-t border-border pt-6 text-xs text-muted-foreground">
                {altLink}
              </p>
            )}
          </div>
        </main>
      </div>
      <AuthPanel aside={aside} />
    </div>
  );
}
